import { useState } from 'react';
import StatusBar from 'common/components/StatusBar';
import OfflineModeInfoModal from 'common/components/OfflineModeInfoModal';
import { ReactComponent as InfoIcon } from 'common/images/info-tooltip.svg';
import { useCloudReachability } from 'common/hooks';
import { buildCloudUrl } from 'common/utils/buildCloudUrl';
import { resolveCloudUrl } from 'common/utils/resolveCloudUrl';
import {
  KEEP_TAB_OPEN_TEST_ID,
  OFFLINE_MODE_BANNER_INFO_LABEL,
  OFFLINE_MODE_BANNER_TEXT,
  OFFLINE_MODE_KEEP_TAB_OPEN,
  RETURN_TO_CLOUD_LABEL,
  RETURN_TO_CLOUD_UNAVAILABLE_LABEL,
} from 'common/utils/offlineMode/constants';
import { mobxInjectSelect } from 'common/utils';
import BEM from 'common/bem';

const classes = BEM.with('LocalServerStatusBar');

const cloudUrl = resolveCloudUrl({ buildTimeCloudUrl: process.env.CLOUD_URL });

type OwnProps = {
  bannerVariant?: string;
  'data-test-id'?: string;
};

type MobxProps = {
  networkStatus: string;
  isLocalServer: boolean;
  currentOrganizationId: string | null;
  isOfflineModeActive: (orgToken: string) => boolean;
};

function LocalServerStatusBar({
  bannerVariant,
  'data-test-id': dataTestId,
  networkStatus,
  isLocalServer,
  currentOrganizationId,
  isOfflineModeActive,
}: OwnProps & MobxProps) {
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);

  const cloudReachable = useCloudReachability(cloudUrl, {
    enabled: isLocalServer && networkStatus === 'ONLINE',
  });

  if (!isLocalServer) return null;

  const isOfflineModeActivated = currentOrganizationId
    ? isOfflineModeActive(currentOrganizationId)
    : false;

  const canReturnToCloud = cloudReachable;

  const handleReturnToCloud = () => {
    if (!canReturnToCloud) return;
    window.location.href = buildCloudUrl(cloudUrl, currentOrganizationId);
  };

  return (
    <StatusBar
      variant={canReturnToCloud ? 'success' : 'error'}
      className={classes()}
      data-test-id={dataTestId}
      data-variant={bannerVariant}
    >
      <span className={classes('message')}>{OFFLINE_MODE_BANNER_TEXT}</span>
      {isOfflineModeActivated && (
        <span className={classes('keep-tab-open')} data-test-id={KEEP_TAB_OPEN_TEST_ID}>
          {OFFLINE_MODE_KEEP_TAB_OPEN}
        </span>
      )}
      <button
        type="button"
        className={classes('info-button')}
        aria-label={OFFLINE_MODE_BANNER_INFO_LABEL}
        data-test-id="offline-mode-banner-info"
        onClick={() => setIsInfoModalOpen(true)}
      >
        <InfoIcon className={classes('info-icon')} aria-hidden="true" />
      </button>
      <button
        type="button"
        className={classes('button', { disabled: !canReturnToCloud })}
        aria-disabled={!canReturnToCloud}
        aria-label={canReturnToCloud ? RETURN_TO_CLOUD_LABEL : RETURN_TO_CLOUD_UNAVAILABLE_LABEL}
        data-test-id="return-to-cloud"
        onClick={canReturnToCloud ? handleReturnToCloud : undefined}
      >
        {RETURN_TO_CLOUD_LABEL}
      </button>
      <OfflineModeInfoModal isOpen={isInfoModalOpen} onClose={() => setIsInfoModalOpen(false)} />
    </StatusBar>
  );
}

export default mobxInjectSelect<OwnProps, MobxProps>({
  networkStatusStore: ['networkStatus'],
  messengerStore: ['isLocalServer', 'currentOrganizationId'],
  activationStateStore: ['isOfflineModeActive'],
})(LocalServerStatusBar);
